// @flow
import _ from 'lodash'
import React, { Component } from 'react'
import { translations, translateFunc } from '../../shared/localize'
import ErrorText from '../controls/ErrorText'
import Tooltip from '../Tooltip'
import styled, { type StyledComponent } from 'styled-components'
import type { OptionType } from '../types'
import { alertColor } from '../controls/colors'
import getJson from '../../services/getJson'
import SelectWithMatcher from '../controls/SelectWithMatcher'

const LocationsFastLinks: StyledComponent<Object, any, any> = styled.div`
  margin-top: 10px;
  width: 380px;
  display: inline-block;
  font-family: 'PT Sans Caption', sans-serif;
  font-size: 11px;
  color: #2da1c4;
`
const LocationsFastLinksMenu: StyledComponent<Object, any, any> = styled.div`
  padding: 10px;
  text-align: left;
  width: 120px;
`
const LocationsTriangle: StyledComponent<Object, any, any> = styled.span`
  width: 0;
  height: 0;
  border-top: 5px solid #2da1c4;
  border-left: 3px solid transparent;
  border-right: 3px solid transparent;
  position: absolute;
  top: 5px;
  right: -10px;
`
const LocationsFastLink: StyledComponent<Object, any, any> = styled.a`
  color: #2da1c4;
  border-bottom: 1px dashed #2da1c4;
  margin: 0 10px 0 0;
  text-decoration: none;
  display: inline-block;
  position: relative;
  font-family: 'PT Sans Caption', sans-serif;
  font-size: 11px;
  white-space: nowrap;

  &:hover {
    color: ${alertColor};
    border-bottom-color: ${alertColor};
    ${LocationsTriangle} {
      border-top-color: ${alertColor};
    }
  }
`
const ClearBoth: StyledComponent<Object, any, any> = styled.div`
  clear: both;
`
const DropdownFastLinkWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-bottom: 5px;
`
const LocationOption: StyledComponent<Object, any, any> = styled.div`
  padding: 2px;
`
const LocationLabel: StyledComponent<Object, any, any> = styled.span`
  border-radius: 2px;
  color: rgb(32, 153, 202);
  font-size: 85%;
  overflow: hidden;
  padding: 3px 3px 6px 6px;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
`

export type Location = {|
  id: number,
  value: number,
  label: string
|}

type Props = {|
  errors: Array<string>,
  quickLocationOptions: Array<OptionType>,
  dropdownLocationOptions: Array<OptionType>,
  locationIds: Array<number>,
  preloadedLocations: Array<Location>,
  ajaxUrl: string,
  ajaxDelay: number,
  locationType: string,
  className: string,
  onLocationsLoad: (Array<Location>) => void,
  onChange: (Object) => void
|}

type State = {|
  options: Array<Location>,
  isLoading: boolean
|}

const t = translateFunc(translations.locations_select)

const matcherT = {
  placeholder: 'Например:\nМосква\nЛенинградская область\nРоссия\n',
  subtitle: 'Укажите названия городов, регионов или стран. Каждое - с новой строки',
  title: 'Массовое добавление локаций'
}

const matcherRequestPath = '/audiences/locations_by_names'

export default class LocationsSelect extends Component<Props, State> {
  debouncedSearch: Object
  searchString: string
  emptySearchStringCache: Array<Location>
  responseCache: Object

  constructor(props: Props) {
    super(props)
    this.debouncedSearch = _.debounce(this.search, props.ajaxDelay ? props.ajaxDelay : 0)
    this.searchString = ''
    this.emptySearchStringCache = []
    this.responseCache = {}
    this.state = { options: [], isLoading: false }
    window.lastLocationsSelect = this // crutch
  }

  render() {
    return (
      <div className={this.props.className}>
        <SelectWithMatcher
          matcherClassName="t-LocationNameMatcherModal"
          values={this.values()}
          options={this.state.options}
          isLoading={this.state.isLoading}
          defaultOptions
          matcherMaxElementsCount={30}
          matcherPlaceholder={matcherT.placeholder}
          matcherRequestPath={matcherRequestPath}
          matcherSubtitle={matcherT.subtitle}
          matcherTitle={matcherT.title}
          buildUniqOptions={(found, selectValues) => {
            const locations = _.map(found, (location) => {
              return {
                id: location.id,
                value: location.id,
                label: location.label
              }
            })
            return _.filter(locations, (o) => {
              return !_.find(selectValues, { id: o.id })
            })
          }}
          matchElements={(found, inputNames) => {
            return _.filter(found, (location) => {
              return _.includes(inputNames, location.label)
            })
          }}
          onBeforeComponentValuesChange={(locations) => this.props.onLocationsLoad(locations)}
          onComponentValuesChange={(values) => this.componentValuesChanged(values)}
          onFocus={() => this.onFocus()}
          onInputChange={(q) => {
            this.searchString = q
            this.debouncedSearch()
          }}
          onParseFromMatcherInput={(input) => {
            return _.reject(input.split(/[\t\r\n,]/), _.isEmpty)
          }}
          optionRenderer={(option) => this.optionRenderer(option)}
        />
        <ErrorText errors={this.props.errors} />
        <ClearBoth />
        <LocationsFastLinks>
          {this.props.quickLocationOptions.map((option) => this.renderLocationLink(option))}
        </LocationsFastLinks>
        <Tooltip
          isInteractive
          isTriggeredByClick
          content={
            <LocationsFastLinksMenu>
              {this.props.dropdownLocationOptions.map((option, index) => {
                return (
                  <DropdownFastLinkWrapper key={index}>
                    {this.renderLocationLink(option)}
                  </DropdownFastLinkWrapper>
                )
              })}
            </LocationsFastLinksMenu>
          }
        >
          <LocationsFastLink href="#" onClick={(e) => e.preventDefault()}>
            {t('more_cities')}
            <LocationsTriangle />
          </LocationsFastLink>
        </Tooltip>
      </div>
    )
  }

  componentValuesChanged(values: Array<OptionType>) {
    this.props.onChange(_.map(values, (value) => value.id))
  }

  renderLocationLink(option: OptionType) {
    return (
      <LocationsFastLink key={option.id} href="#" onClick={(e) => this.addLocationId(e, option.id)}>
        {option.name}
      </LocationsFastLink>
    )
  }

  addLocationId(e: any, id: number) {
    e.preventDefault()
    this.props.onChange(_.uniq(_.concat(this.props.locationIds, id)))
  }

  onFocus() {
    if (this.emptyStringRequested()) {
      return this.setState({ options: this.emptySearchStringCache })
    }

    this.debouncedSearch()
  }

  emptyStringRequested() {
    return !_.isEmpty(this.emptySearchStringCache)
  }

  search() {
    const { locationType } = this.props
    let input = this.searchString

    if (_.isEmpty(input) && this.emptyStringRequested()) return

    if (this.responseCache[input]) {
      return this.setState({ options: this.responseCache[input] })
    }

    this.setState({ options: [], isLoading: true })
    getJson(this.props.ajaxUrl, { search: input, location_type: locationType }).then((response) => {
      let options = _.map(response, (location) => {
        return {
          id: location.id,
          value: location.id,
          label: location.name
        }
      })

      this.props.onLocationsLoad(options)

      if (_.isEmpty(input)) {
        this.emptySearchStringCache = options
      }

      this.responseCache[input] = options
      this.setState({ options: options, isLoading: false })
    })
  }

  values(): Array<Location> {
    return _.compact(
      _.map(this.props.locationIds, (id) => _.find(this.props.preloadedLocations, { id: id }))
    )
  }

  optionRenderer(option: Location) {
    return (
      <LocationOption>
        <LocationLabel>{option.label}</LocationLabel>
      </LocationOption>
    )
  }
}
